import { useState } from "react";
import { ActivityItemShell } from "../../shell/ActivityItemShell";
import { ActivityShellAction } from "../../shell/ActivityShellTypes";
import { ActivityPluginProps } from "../../registry/ActivityPluginTypes";
import { usePinActivityHook } from "../../../hooks/pinActivityHook";
import {
  deleteActivityAction,
  DisplayFileIcon
} from "../../../ActivityHistory";
import { TranslationService } from "../../../../../../services/TranslationService";
import CompanyService from "../../../../../../services/CompanyService";
import ActivityService from "../../../../../../services/ActivityService";
import InvoiceService from "../../../../../../services/InvoiceService";
import FloatingPanelService from "../../../../../shared/FloatingPanel";
import TooltipComponent from "../../../../../shared/TooltipComponent";
import { SafeHtml } from "../../../../../../components/SafeHtml";
import NewComment, { LabelActionText } from "../../../components/NewComment";
import Table from "../../../../../shared/Table";
import TableContext, {
  TableContextValues
} from "../../../../../task/TableContext";
import InvoiceTableItemReadOnly from "../../../../invoice/InvoiceTableItemReadOnly";
import { ActivityGetResponse } from "../../../entities/AcitivityGetResponse";
import { ActivityHistoryType } from "../../../../../shared/enums";
import { SortDirection } from "../../../../../shared/entities/Sort";
import {
  formatDateDigits,
  formatDateTimeDigits
} from "../../../../../../utils/FormatUtils";
import { restTimezone } from "../../../../../../utils/ParseUtils";
import { addIf, isNullOrWhitespace } from "../../../../../../utils/Utils";
import iconComment from "../../../../../../assets/img/iconComment.svg";
import { SanitizeUtils } from "../../../../../../utils/SanitizeUtils";

const DisplayActivityType = (data: object | { typeName?: string }) => {
  if ("typeName" in data && data.typeName !== undefined) {
    return <>{TranslationService.getTranslation(data.typeName)}</>;
  }
  return <></>;
};

const DisplayActivityName = (
  data: { ActivityType?: { ActivityTypeName: string } } | object
) => {
  if (
    "ActivityType" in data &&
    data.ActivityType?.ActivityTypeName !== undefined
  ) {
    return <> - {data.ActivityType.ActivityTypeName}</>;
  }
  return <></>;
};

const DisplayGroupName = (
  data: { groupName?: string; Group?: { Name: string } } | object
) => {
  if (
    "groupName" in data &&
    data.groupName &&
    (data.groupName as string).length > 0
  ) {
    return (
      <div style={{ marginRight: 10 }}>
        <strong>{CompanyService.getGroupName()}</strong>:{" "}
        <em>{data.groupName as string}</em>
      </div>
    );
  }
  if ("Group" in data && (data as { Group?: { Name: string } }).Group?.Name) {
    return (
      <div style={{ marginRight: 10 }}>
        <strong>{CompanyService.getGroupName()}</strong>:{" "}
        <em>{(data as { Group: { Name: string } }).Group.Name}</em>
      </div>
    );
  }
  return <></>;
};

const DisplayTagIcon = (
  data: { tagList: string } | { tags: string } | object,
  list: boolean
) => {
  let tags = "";
  if ("tagList" in data) tags = data.tagList;
  else if ("tags" in data) tags = data.tags;
  if (!tags || tags.length === 0) return <></>;
  return (
    <div className="d-flex align-items-center">
      <TooltipComponent title={TranslationService.translate.Tags}>
        <i className="fa fa-tag activity-icon-color my-2" />
      </TooltipComponent>
      {list && (
        <div className="d-flex align-items-center gap-2">
          {tags.split(",").map((tag, i) => (
            <div key={i}>
              <LabelActionText bgColor={false} text={tag} action={() => {}} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DisplayIosContent = (fixedIos: ActivityGetResponse["ios"]) => {
  if (!fixedIos || fixedIos.length === 0) return null;
  if (!CompanyService.getSetting("enablemessageiolink")) return null;

  const tableHeaders = [...InvoiceService.getTableHeaders()];
  const tableValues = new TableContextValues();
  tableValues.error = false;
  tableValues.response = { list: fixedIos };
  tableValues.loading = false;
  tableValues.sort = { sortColumn: "", sortDirection: SortDirection.Ascending };
  tableValues.setSortColumn = () => Boolean(4);

  return (
    <>
      <div className="d-flex align-items-center">
        <TooltipComponent
          title={TranslationService.translate.ManagementAssociatedWithInvoices}
        >
          <i className="far fa-notes activity-icon-color m-2" />
        </TooltipComponent>
        <h6 className="linkedInvoicesComments pb-2">
          {TranslationService.translate.LinkedInvoices}
        </h6>
      </div>
      <TableContext.Provider value={tableValues}>
        <Table
          headers={tableHeaders}
          item={InvoiceTableItemReadOnly(undefined)}
        />
      </TableContext.Provider>
    </>
  );
};

export const ActivityCommentCard = ({ data, reload }: ActivityPluginProps) => {
  const { translate } = TranslationService;
  const [fixedIos, setFixedIos] = useState<ActivityGetResponse["ios"]>();
  const reloadSimple = () => reload();
  const { icon, canPin, togglePinActivity, tooltip } = usePinActivityHook({
    data,
    reload: reloadSimple
  });

  const onExpand = async () => {
    if (!("ID" in data)) return;
    const result = await ActivityService.get(data.ID);
    if (result instanceof Error) return;
    setFixedIos(result.ios && result.ios.length > 0 ? result.ios : undefined);
  };

  const openEditCommentModal = (handleClose?: () => void) =>
    FloatingPanelService.showPanel({
      children: (
        <NewComment
          clientid={data.PersonId.toString()}
          data={data}
          reload={reloadSimple}
          handleClose={handleClose}
        />
      ),
      title: translate.EditComment,
      height: 800,
      width: 600
    });

  const actions: ActivityShellAction[] = [
    ...addIf(canPin, { icon, onClick: togglePinActivity, tooltip })
  ];

  if (CompanyService.canDo("editcomment")) {
    actions.push({
      icon: "fa-pen text-secondary",
      onClick: openEditCommentModal,
      tooltip: translate.Edit
    });
  }

  if (
    CompanyService.canDo("deletecomment") &&
    (CompanyService.getIsAdmin() ||
      (data.Creator ?? 0) === parseInt(CompanyService.getUserid()))
  ) {
    actions.push(
      deleteActivityAction(
        data.ID,
        translate.CommentDeleteTitle,
        translate.CommentDeleteMessage,
        reloadSimple
      )
    );
  }

  const displayUsername = isNullOrWhitespace(data.Username)
    ? isNullOrWhitespace(data.CreatorUsername)
      ? data.CollectorName
      : data.CreatorUsername
    : data.Username;

  const date = new Date(data.date);
  const iconNode = (
    <img className="img-fluid" src={iconComment} alt="icon comment" />
  );

  const headerNode = (
    <div className="type">
      {DisplayActivityType(data)}
      <span className="creator">
        {translate.From.toLowerCase()}{" "}
        {(displayUsername ?? "").split(", ").reverse().join(" ")}
        {DisplayActivityName(data)}
      </span>
    </div>
  );

  const contentNode = (
    <>
      {data.Subject && (
        <small>
          <em>
            <strong>
              {translate.Subject}: {data.Subject}
            </strong>
          </em>
        </small>
      )}
      <SafeHtml
        html={ActivityService.replaceUsersToHtml(data.Content)}
        style={{ overflow: "auto" }}
      />
      <div className="d-flex align-items-center">
        {DisplayGroupName(data)}
        {DisplayFileIcon(data, ActivityHistoryType.comment, false)}
        {DisplayTagIcon(data, false)}
        {data.hasIOs && (
          <TooltipComponent title={translate.ManagementAssociatedWithInvoices}>
            <i className="far fa-notes activity-icon-color my-2" />
          </TooltipComponent>
        )}
      </div>
    </>
  );

  const dateNode = (
    <div className="dateTertiaryQ">
      {date.getHours() === 0 && date.getMinutes() === 0
        ? formatDateDigits(date)
        : formatDateTimeDigits(restTimezone(date))}
    </div>
  );

  const expandedContent = (
    <>
      {DisplayFileIcon(data, ActivityHistoryType.comment, true)}
      {DisplayTagIcon(data, true)}
      {fixedIos && DisplayIosContent(fixedIos)}
    </>
  );

  const compactLine = (
    <>
      <strong>{displayUsername}</strong>
      {" - "}
      {SanitizeUtils.htmlToPlainText(data.Content ?? "")}
    </>
  );

  return (
    <ActivityItemShell
      cardClassName="historyCard pointer pb-1"
      iconNode={iconNode}
      headerNode={headerNode}
      dateNode={dateNode}
      content={contentNode}
      actions={actions}
      expandable={true}
      expandedContent={expandedContent}
      onExpand={onExpand}
      compactLine={compactLine}
    />
  );
};
